import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabase';
import type { OrgLevel, PoliticalOrg } from '../types/PoliticalOrg';

type UsePoliticalOrgsParams = {
  level: OrgLevel;
  stateCode?: string | null;
};

type UsePoliticalOrgsResult = {
  orgs: PoliticalOrg[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

export function usePoliticalOrgs({
  level,
  stateCode,
}: UsePoliticalOrgsParams): UsePoliticalOrgsResult {
  const [orgs, setOrgs] = useState<PoliticalOrg[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const normalizedStateCode = useMemo(() => {
    return stateCode ? stateCode.trim().toUpperCase() : null;
  }, [stateCode]);

  const fetchOrgs = async () => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('political_orgs')
        .select('*')
        .eq('is_active', true)
        .eq('is_verified', true)
        .eq('org_type', 'party')
        .eq('level', level)
        .not('donation_url', 'is', null)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (level === 'state') {
        if (!normalizedStateCode) {
          setOrgs([]);
          setLoading(false);
          return;
        }

        query = query.eq('state_code', normalizedStateCode);
      } else {
        query = query.is('state_code', null);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setOrgs((data ?? []) as PoliticalOrg[]);
    } catch (err) {
      console.error('Failed to fetch political organizations:', err);
      setError('Unable to load organizations. Please try again.');
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, normalizedStateCode]);

  return {
    orgs,
    loading,
    error,
    refetch: fetchOrgs,
  };
}